const MAX_OAUTH_VALUE_LENGTH = 2048;

export const MCP_AUTHORIZE_PATH = "/mcp/authorize";
export const MCP_AUTHORIZE_RETURN_STORAGE_KEY = "mcpAuthorizeReturnPath";

export const MCP_OAUTH_REGISTER_FUNCTION = "mcpOAuthRegister";
export const MCP_OAUTH_CONSENT_FUNCTION = "mcpOAuthConsent";
export const MCP_LIST_CONNECTIONS_FUNCTION = "listMcpConnections";
export const MCP_REVOKE_CONNECTION_FUNCTION = "revokeMcpConnection";

export const MCP_OAUTH_PERMISSION_CODE = "advertiserIDToChangeNoPermission";
export const MCP_OAUTH_ADVERTISER_NOT_FOUND_CODE = "advertiserNotFound";
export const MCP_OAUTH_CODE_CHALLENGE_METHOD = "S256";

const isBoundedString = (value) =>
  typeof value === "string" &&
  value.length > 0 &&
  value.length <= MAX_OAUTH_VALUE_LENGTH;

const readOptionalQuery = (searchParams, name) => {
  const value = searchParams.get(name);
  if (value == null || value === "") return undefined;
  return value;
};

export const readMcpAuthorizeQuery = (searchParams) => ({
  clientId: searchParams.get("client_id")?.trim() || "",
  redirectUri: searchParams.get("redirect_uri")?.trim() || "",
  codeChallenge: searchParams.get("code_challenge")?.trim() || "",
  codeChallengeMethod: searchParams.get("code_challenge_method")?.trim() || "",
  state: readOptionalQuery(searchParams, "state"),
  resource: readOptionalQuery(searchParams, "resource"),
});

const BANNED_REDIRECT_SCHEMES = new Set([
  "javascript:",
  "data:",
  "file:",
  "blob:",
  "about:",
  "vbscript:",
]);

// Mirrors the backend: native clients such as Cursor use a custom scheme.
const isSafeRedirectUrl = (value) => {
  try {
    const url = new URL(value);
    if (url.username || url.password) return false;
    return !BANNED_REDIRECT_SCHEMES.has(url.protocol.toLowerCase());
  } catch {
    return false;
  }
};

/**
 * Why the authorize query cannot continue.
 * `redirect` and `invalid` both stay on the error screen.
 */
export const getMcpAuthorizeQueryIssue = (query) => {
  if (
    !isBoundedString(query?.clientId) ||
    !isBoundedString(query?.redirectUri) ||
    !isBoundedString(query?.codeChallenge)
  ) {
    return "invalid";
  }

  if (query.state !== undefined && !isBoundedString(query.state)) {
    return "invalid";
  }

  if (query.resource !== undefined && !isBoundedString(query.resource)) {
    return "invalid";
  }

  if (!isSafeRedirectUrl(query.redirectUri)) return "redirect";

  if (query.codeChallengeMethod !== MCP_OAUTH_CODE_CHALLENGE_METHOD) {
    return "invalid";
  }

  return null;
};

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

// RFC 8252 §7.3, same rule as the backend: a loopback redirect may use any
// port, but scheme, host, path, and query must match.
const loopbackRedirectMatches = (requested, registered) => {
  let requestedUrl;
  let registeredUrl;
  try {
    requestedUrl = new URL(requested);
    registeredUrl = new URL(registered);
  } catch {
    return false;
  }
  return (
    LOOPBACK_HOSTS.has(requestedUrl.hostname) &&
    requestedUrl.protocol === registeredUrl.protocol &&
    requestedUrl.hostname === registeredUrl.hostname &&
    requestedUrl.pathname === registeredUrl.pathname &&
    requestedUrl.search === registeredUrl.search
  );
};

export const isRedirectUriRegistered = ({ redirectUri, redirectUris }) =>
  Array.isArray(redirectUris) &&
  redirectUris.some(
    (registered) =>
      registered === redirectUri ||
      loopbackRedirectMatches(redirectUri, registered)
  );

export const parseRegisteredClient = (data) => {
  if (!data || typeof data !== "object") return null;

  const redirectUris = Array.isArray(data.redirectUris)
    ? data.redirectUris.filter((item) => typeof item === "string" && item)
    : [];
  const clientName =
    typeof data.clientName === "string" ? data.clientName.trim() : "";

  if (!clientName || redirectUris.length === 0) return null;

  return {
    clientID: typeof data.clientID === "string" ? data.clientID : "",
    clientName,
    redirectUris,
  };
};

export const buildMcpConsentPayload = ({
  clientId,
  redirectUri,
  codeChallenge,
  state,
  resource,
  advertiserID,
}) => {
  const payload = {
    client_id: clientId,
    redirect_uri: redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: MCP_OAUTH_CODE_CHALLENGE_METHOD,
    advertiserID,
  };

  if (typeof state === "string" && state.length > 0) payload.state = state;
  if (typeof resource === "string" && resource.length > 0) {
    payload.resource = resource;
  }

  return payload;
};

export const buildAccessDeniedRedirect = ({ redirectUri, state }) => {
  let url;
  try {
    url = new URL(redirectUri);
  } catch {
    return null;
  }

  if (!isSafeRedirectUrl(url.href)) return null;

  url.searchParams.set("error", "access_denied");
  if (typeof state === "string" && state.length > 0) {
    url.searchParams.set("state", state);
  }

  return url.href;
};

export const isApprovedConsentRedirect = ({ redirectUri, redirectUrl }) => {
  if (typeof redirectUrl !== "string" || !redirectUrl) return false;
  if (typeof redirectUri !== "string" || !redirectUri) return false;

  let allowed;
  let target;
  try {
    allowed = new URL(redirectUri);
    target = new URL(redirectUrl);
  } catch {
    return false;
  }

  if (!isSafeRedirectUrl(target.href) || !isSafeRedirectUrl(allowed.href)) {
    return false;
  }
  if (allowed.protocol !== target.protocol) return false;
  if (allowed.host !== target.host) return false;
  if (allowed.pathname !== target.pathname) return false;

  for (const [key, value] of allowed.searchParams.entries()) {
    if (target.searchParams.get(key) !== value) return false;
  }

  return true;
};

export const isMcpOauthClientCode = (code) =>
  typeof code === "string" && code.startsWith("mcpOauth");

export const getMcpOAuthErrorKind = ({
  status,
  code,
  isNetwork = false,
} = {}) => {
  if (isNetwork) return "unexpected";
  if (code === MCP_OAUTH_PERMISSION_CODE) return "permission";
  if (code === MCP_OAUTH_ADVERTISER_NOT_FOUND_CODE) return "notFound";
  if (isMcpOauthClientCode(code) && /redirect/i.test(code)) return "redirect";
  if (isMcpOauthClientCode(code) && /client/i.test(code)) return "client";
  if (isMcpOauthClientCode(code)) return "invalid";
  if (status === 404) return "notFound";
  if (status === 403) return "permission";
  if (status === 400) return "invalid";
  return "unexpected";
};

/**
 * 4xx and known OAuth client codes stay out of Sentry.
 * 5xx and network failures are reported.
 */
export const isExpectedMcpOAuthFailure = ({
  status,
  code,
  isNetwork = false,
} = {}) => {
  if (isNetwork) return false;
  if (typeof status === "number" && status >= 500) return false;
  if (typeof status === "number" && status >= 400 && status < 500) return true;
  if (
    code === MCP_OAUTH_PERMISSION_CODE ||
    code === MCP_OAUTH_ADVERTISER_NOT_FOUND_CODE ||
    isMcpOauthClientCode(code)
  ) {
    return true;
  }
  return false;
};

export const parseMcpAuthorizeReturnPath = (value) => {
  if (typeof value !== "string") return null;
  const withoutHash = value.split("#")[0];
  if (withoutHash.includes("\\") || withoutHash.includes("://")) return null;
  if (
    withoutHash !== MCP_AUTHORIZE_PATH &&
    !withoutHash.startsWith(`${MCP_AUTHORIZE_PATH}?`)
  ) {
    return null;
  }
  return withoutHash;
};

const getSessionStorage = (storage) => {
  if (storage) return storage;
  if (typeof sessionStorage === "undefined") return null;
  return sessionStorage;
};

export const rememberMcpAuthorizeReturnPath = ({
  pathname,
  search,
  storage,
} = {}) => {
  const path = parseMcpAuthorizeReturnPath(`${pathname || ""}${search || ""}`);
  if (!path) return;

  const target = getSessionStorage(storage);
  if (!target) return;

  try {
    target.setItem(MCP_AUTHORIZE_RETURN_STORAGE_KEY, path);
  } catch {
    // Private mode or quota. The login location state still carries the path.
  }
};

export const readMcpAuthorizeReturnPath = ({
  locationStateFrom,
  storage,
} = {}) => {
  const target = getSessionStorage(storage);
  let stored = null;
  try {
    stored = target?.getItem(MCP_AUTHORIZE_RETURN_STORAGE_KEY) || null;
  } catch {
    stored = null;
  }

  const storedPath = parseMcpAuthorizeReturnPath(stored);
  if (storedPath) return storedPath;

  return parseMcpAuthorizeReturnPath(
    `${locationStateFrom?.pathname || ""}${locationStateFrom?.search || ""}`
  );
};

export const clearMcpAuthorizeReturnPath = ({ storage } = {}) => {
  const target = getSessionStorage(storage);
  if (!target) return;
  try {
    target.removeItem(MCP_AUTHORIZE_RETURN_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const readMcpConnectionTime = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 0 && value < 1e12 ? value * 1000 : value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (/^\d+$/.test(trimmed)) {
      const asNumber = Number(trimmed);
      return asNumber > 0 && asNumber < 1e12 ? asNumber * 1000 : asNumber;
    }
    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

export const sortMcpConnectionsNewestFirst = (connections) => {
  if (!Array.isArray(connections)) return [];
  return connections.toSorted(
    (left, right) =>
      readMcpConnectionTime(right?.creationTime) -
      readMcpConnectionTime(left?.creationTime)
  );
};

export const sanitizeMcpConnection = (connection) => ({
  keyID: typeof connection?.keyID === "string" ? connection.keyID : "",
  clientID: typeof connection?.clientID === "string" ? connection.clientID : "",
  clientName:
    typeof connection?.clientName === "string" ? connection.clientName : "",
  advertiserID:
    typeof connection?.advertiserID === "string" ? connection.advertiserID : "",
  isRevoked: connection?.isRevoked === true,
  creationTime: connection?.creationTime ?? null,
  updateTime: connection?.updateTime ?? null,
});
